import { motion } from 'framer-motion';
import { t } from 'i18next';
import { isEmpty, throttle } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Map as KakaoMap } from 'react-kakao-maps-sdk';
import { useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import ToggleLikeLocation from '../components/api/mutations/ToggleLikeLocation';
import {
  useLocationEventsQuery,
  useLocationQuery,
  useNearByLocationQuery
} from '../components/api/queries/useLocationsQuery';
import PostImagesBox from '../components/community/PostImagesBox';
import { CustomMapMarker } from '../components/offlineLocation/CustomMapMarker';
import LocationBottomBar from '../components/offlineLocation/LocationBottomBar';
import LocationCompletedEvent from '../components/offlineLocation/LocationCompletedEvent';
import LocationOngoingEvent from '../components/offlineLocation/LocationOngoingEvent';
import LocationPendingEvent from '../components/offlineLocation/LocationPendingEvent';
import NearbyLocationBox from '../components/offlineLocation/NearbyLocationBox';
import GridSkeletons from '../components/skeletons/GridSkeletons';
import ProfileAvatar from '../components/thumbnails/ProfileAvatar';
import { Location } from '../domain/models/locations/Location';
import { ReactComponent as LikeLineIcon } from '../icons/like.line.svg';
import { ReactComponent as LikeGradientShape } from '../icons/likeGradientShape.svg';
import { ReactComponent as MapFillIcon } from '../icons/map.fill.svg';
import { ReactComponent as MoveIcon } from '../icons/move_icon.svg';
import { ReactComponent as XIcon } from '../icons/sns.x.svg';
import { ReactComponent as CloseIcon } from '../icons/xmark.svg';
import EventPosts from '../presentation/components/event-detail/EventPosts';
import FootprintsBox from '../presentation/components/event-detail/FootprintsBox';
import type { ILocationEventsResponseBody } from '../types/response/ILocationEventsResponseBody';
import type { ILocationItemResponseBody } from '../types/response/ILocationItemResponseBody';
import type { ICoordinates } from '../types/types';
import { getSiteType } from '@pocapan/shared/utils/SiteTypes';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import moveKakaoMap from '@pocapan/shared/utils/moveKakaoMap';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import useKakaoLoader from '@pocapan/shared/utils/useKakaoLoader';
import { useColorMode } from '@chakra-ui/react';
import StickyHeader from '../components/StickyHeader';

const OfflineLocationDetailPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleHistoryBack = useHandleHistoryBack();
  const { colorMode } = useColorMode();

  useKakaoLoader(); // Kakao JavaScript API 호출

  const { location_id } = useParams();

  const [location, setLocation] = useState<Location>();
  const [liked, setLiked] = useState<boolean>(location?.isLiked ?? false);
  const [likeCount, setLikeCount] = useState<number>(location?.likesCount ?? 0);
  const [nearByLocations, setNearByLocations] = useState<ILocationItemResponseBody[]>([]);
  const [eventsPending, setEventsPending] = useState<ILocationEventsResponseBody[]>([]);
  const [eventsCompleted, setEventsCompleted] = useState<ILocationEventsResponseBody[]>([]);
  const [isShowBottomToast, setIsShowBottomToast] = useState(true);

  const { data: locationData } = useLocationQuery({ locationId: location_id });
  const { data: nearByLocationData } = useNearByLocationQuery({
    locationId: location_id,
    distanceInMeters: 2000
  });
  const { data: EventPendingData } = useLocationEventsQuery({
    locationId: location_id,
    status: 'pending'
  });
  const { data: EventCompletedData } = useLocationEventsQuery({
    locationId: location_id,
    status: 'completed'
  });
  const lastScrollY = useRef(0);

  const SELECTED_MARKER_COLOR_LIGHT = '#5C3DFA';
  const SELECTED_SHADOW_COLOR_LIGHT = '0px 0px 16.8px 6px #5C3DFA';
  const SELECTED_MARKER_COLOR_DARK = '#786CF6';
  const SELECTED_SHADOW_COLOR_DARK = '0px 0px 16.8px 6px #786CF6';

  useEffect(() => {
    if (locationData) {
      setLocation(Location.fromResponse(locationData.location));
    }
  }, [locationData]);

  useEffect(() => {
    if (location) {
      setLiked(location.isLiked);
      setLikeCount(location.likesCount ?? 0);
    }
  }, [location]);

  useEffect(() => {
    if (nearByLocationData) {
      setNearByLocations(nearByLocationData.locations);
    }
  }, [nearByLocationData]);

  useEffect(() => {
    if (EventPendingData) {
      setEventsPending(EventPendingData.events);
    }
  }, [EventPendingData]);

  useEffect(() => {
    if (EventCompletedData) {
      setEventsCompleted(EventCompletedData.events);
    }
  }, [EventCompletedData]);

  useEffect(() => {
    const handlePostCreationComplete = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || isEmpty(event.data?.type)) return;

      if (event.data.type === 'post-creation-complete') {
        const post = event.data.params?.post;
        if (post) {
          queryClient.invalidateQueries('LocationPosts');
        }
      }

      // if (event.data?.type === 'report-location-complete') {
      //   showReportToastMessage();
      //   queryClient.invalidateQueries(['OfflineLocation']);
      // }
    };

    const throttledReceiveMessage = throttle(handlePostCreationComplete, 1000);
    window.addEventListener('message', throttledReceiveMessage);

    return () => {
      window.removeEventListener('message', throttledReceiveMessage);
    };
  }, [queryClient]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY.current + 200) {
        // Scrolling down
        setIsShowBottomToast(false);
      } else if (currentScrollY < lastScrollY.current - 200) {
        // Scrolling up
        setIsShowBottomToast(true);
      }

      lastScrollY.current = currentScrollY;
    }, 500);

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  //
  // const showReportToastMessage = () => {
  //   toast({
  //     position: 'top',
  //     duration: 2000,
  //     render: () => (
  //       <BaseToastMessageBox
  //         position={'top'}
  //         startElement={
  //           <div className="flex items-center justify-center rounded-full w-[15px] h-[15px] bg-onSuccess-light dark:bg-onSuccess-dark">
  //             <CheckIcon className="fill-pure-white w-[15px] h-[15px]" />
  //           </div>
  //         }
  //         message={'소중한 제보가 전송 완료되었어요!'}
  //       />
  //     )
  //   });
  // };

  const handleLikeLocation = async () => {
    if (!location) return;
    const response = await ToggleLikeLocation(location.locationId);
    const data = await response.json();

    if (response?.ok) {
      const responseLiked = data?.location?.isLiked;
      setLiked(responseLiked);
      setLikeCount(data?.location?.likesCount ?? likeCount);
    }
  };

  const naviateEventDetailPage = (locationId: string, eventId: string) => {
    navigate(`/locations/${locationId}/events/${eventId}`);
  };

  const navigateReportFootprint = () => {
    openNewTab(
      `/report/select-artist?reportType=footprint&locationId=${location_id}&spotsCategory=landmark&locationName=${location?.name}&kakaoUID=${location?.kakaoUid}`
    );
  };

  if (!location) {
    return (
      <div className="h-full mt-[40px] safeAreaPaddingTop">
        <GridSkeletons gridSize={1} skeletonSize={1} skeletonHeight={'120px'} paddingX={'22px'} />
      </div>
    );
  }

  const center: ICoordinates = {
    lat: Number(location.latitude),
    lng: Number(location.longitude)
  };

  return (
    <div className="relative h-full safeAreaPaddingTop">
      <StickyHeader
        showTitle={false}
        showBackButton={false}
        leftContent={
          <CloseIcon
            className="w-[24px] h-[24px] fill-pure-black dark:fill-pure-white"
            onClick={() => handleHistoryBack()}
          />
        }
      />
      <div className="relative border-t border-b border-separator2 h-[112px] mt-[54px]">
        <KakaoMap
          center={center}
          style={{
            width: '100%',
            height: '110px',
            position: 'absolute',
            filter: colorMode === 'dark' ? 'invert(1) hue-rotate(180deg)' : ''
          }}
          level={1}
          zoomable={false}
          draggable={false}
        >
          <CustomMapMarker
            location={location}
            onClickMarker={() => {}}
            isSelected={true}
            forceDarkMode={colorMode === 'dark'}
            markerColor={
              colorMode === 'dark' ? SELECTED_MARKER_COLOR_DARK : SELECTED_MARKER_COLOR_LIGHT
            }
            shadowColor={
              colorMode === 'dark' ? SELECTED_SHADOW_COLOR_DARK : SELECTED_SHADOW_COLOR_LIGHT
            }
          />
        </KakaoMap>
      </div>

      <div className="pt-[20px] px-[20px] mt-[0px]">
        <div className="flex mb-[8px] text-2xl text-main-light-2 dark:text-main-dark-2 font-semibold leading-[29px]">
          {location.name}
        </div>
        <div className="flex flex-row ml-[-1px] text-[13px] leading-[16px] text-main-light-4 dark:text-main-dark-4">
          <MapFillIcon className="w-[14px] h-[14px] mb-[2px] mr-[3px] self-center fill-onTheirty-light dark:fill-onSecondary-dark" />
          <span>{location.streetAddress}</span>
        </div>

        {/* 버튼 영역 */}
        <div className="flex flex-row gap-[6px] mt-[20px]">
          {/* 지도앱 열기 - primary 컬러 */}
          <button
            type="button"
            className="w-[100px] h-[39px] px-[18px] py-[11px] rounded-full font-semibold text-[14px] leading-[1] bg-primary-light text-onPrimary flex items-center justify-center"
            onClick={() =>
              moveKakaoMap(Number(location.latitude), Number(location.longitude), location.kakaoUid)
            }
          >
            {t('OfflineLocationDetail.openMapButton', { defaultValue: '지도앱 열기' })}
          </button>

          {/* 찜하기/리뷰남기기 - secondary 컬러 */}
          <button
            type="button"
            className="h-[39px] px-[18px] py-[11px] rounded-full font-semibold text-[14px] leading-[1] bg-secondary-light dark:bg-secondary-dark text-onSecondary-light dark:text-onSecondary-dark flex items-center justify-center"
            onClick={handleLikeLocation}
          >
            {liked ? (
              <LikeGradientShape width={'15px'} height={'15px'} />
            ) : (
              <LikeLineIcon
                className="fill-primary-light dark:fill-primary-dark"
                width={'15px'}
                height={'15px'}
              />
            )}
            <span className="ml-[6px] leading-[1]">{likeCount}</span>
          </button>

          <button
            type="button"
            className="w-[100px] h-[39px] px-[18px] py-[11px] rounded-full font-semibold text-[14px] leading-[1] bg-secondary-light text-onSecondary-light dark:bg-secondary-dark dark:text-onSecondary-dark flex items-center justify-center"
            onClick={() => {
              navigate(`/posts/add?locationId=${location.locationId}&category=FreeTalk`);
            }}
          >
            {t('OfflineLocationDetail.reviewButton', { defaultValue: '리뷰 남기기' })}
          </button>
        </div>
      </div>

      {/* <div className="pl-[20px] mb-2 flex relative space-x-2">
        <PrimaryButton
          text={t('OfflineLocationDetail.openMap')}
          width={'6.25rem'}
          disabled={false}
          icon={null}
          onClick={() => moveKakaoMap(location.latitude, location.longitude, location.kakaoUid)}
        />
        <SecondaryButton
          text={likesCount}
          icon={<img src={placeHeartIcon} alt="place_heart_icon" className="ml-1 cursor-pointer" />}
          width={'4.313rem'}
          leftIcon={null}
          disabled={false}
          onMouseDown={async (e: React.MouseEvent<HTMLImageElement>) => {
            const count = await handlePlaceHeartClick(
              e,
              location.locationId,
              '2',
              setPlaceHeartIcon
            );
            setLikesCount(count);
          }}
        />
        <SecondaryButton
          text={`${t('OfflineLocationDetail.googleReview')} ${location.postsCount ?? 0}`}
          width={'6.813rem'}
          disabled={false}
          leftIcon={null}
          icon={null}
        />
      </div>
       */}
      {location.footprints && (
        <div className="mt-[19px]">
          <FootprintsBox locationId={location.locationId} footprints={location.footprints} />
        </div>
      )}

      <hr className="mx-[20px] mt-[20px] mb-[28px] border-separator-light-1 dark:border-separator-dark-2" />

      <LocationOngoingEvent
        eventsOngoing={location.events || []}
        locationSiteName={getSiteType(location.siteType).name}
        locationSiteIconName={location.siteTypeIconName()}
        onEventClick={naviateEventDetailPage}
        locationId={location.locationId}
      />

      {!isEmpty(nearByLocations) && (
        <>
          <div className="pl-[20px]">
            <span className="text-[15px] font-semibold text-main-light-2 dark:text-main-dark-2">
              {t('OfflineLocationDetail.nearByLocation')}
            </span>
          </div>
          <div className="w-full overflow-x-auto hidden-scroll">
            <div className="inline-block min-w-fit pl-[20px]">
              <NearbyLocationBox nearByLocations={nearByLocations} />
            </div>
          </div>
          <hr className="mx-[20px] mt-[20px] mb-[28px] border-separator-light-1 dark:border-separator-dark-2" />
        </>
      )}

      {!isEmpty(eventsPending) && (
        <>
          <div className="ml-[20px]">
            <span className="text-[15px] font-semibold text-main-light-2 dark:text-main-dark-2">
              {t('OfflineLocationDetail.eventPending')}
            </span>
            <LocationPendingEvent
              eventsPending={eventsPending}
              locationSiteType={location?.siteType}
              onEventClick={naviateEventDetailPage}
              locationId={location?.locationId}
            />
          </div>
          <hr className="mx-[20px] mt-[20px] mb-[28px] border-separator-light-1 dark:border-separator-dark-1" />
        </>
      )}

      {!isEmpty(eventsCompleted) && (
        <>
          <div className="pl-[20px]">
            <span className="text-[15px] font-semibold text-main-light-2 dark:text-main-dark-2">
              {t('OfflineLocationDetail.eventCompleted')}
            </span>
          </div>
          <div className="w-full overflow-x-auto hidden-scroll">
            <div className="inline-block min-w-fit pl-[20px]">
              <LocationCompletedEvent
                eventsCompleted={eventsCompleted}
                locationSiteType={location?.siteType}
                onEventClick={naviateEventDetailPage}
                locationId={location?.locationId}
              />
            </div>
            <hr className="mx-[20px] mt-[20px] mb-[28px] border-separator-light-1 dark:border-separator-dark-1" />
          </div>
        </>
      )}

      <EventPosts locationId={location.locationId} />

      {!isEmpty(location.externalSources) && (
        <div className="border-b-[1px] border-separator-light-1 dark:border-separator-dark-2">
          {' '}
          {location.externalSources.map((externalSource) => {
            return (
              <div key={externalSource.externalSourceId}>
                <div className="flex flex-row mx-[22px] my-5">
                  <div className="flex-none w-[2.125rem] h-[2.125rem]">
                    <ProfileAvatar
                      profileImageUrl={'/assets/icons/ghost_profile_icon.png'}
                      widthAndHeight={'2.125rem'}
                      onClick={null}
                    />
                  </div>
                  <div>
                    <div className="">
                      <div className="flex items-center ml-[10px]">
                        <span className="text-main-light-2 dark:text-main-dark-2 font-semibold mr-[6px] text-[14px]">
                          {externalSource.author?.replace('@', '')}
                        </span>
                        <Link
                          to={externalSource.externalSourceUrl.replace(
                            `/status/${externalSource.externalSourceId}`,
                            ''
                          )}
                        >
                          <XIcon className="cursor-pointer mr-[6px] w-[14px] h-[14px] fill-main-light-4 dark:fill-main-dark-4" />
                        </Link>
                        <span className="text-main-light-4 dark:text-main-dark-4 text-[12px]">
                          {externalSource.authoredDate}
                        </span>
                      </div>
                    </div>

                    <div className="ml-[10px] text-main-light-2 text-[15px] dark:text-main-dark-2">
                      {externalSource.text}
                    </div>

                    <div className="ml-[10px] mb-2 text-[15px] text-[#382599] dark:text-[#998CD9] font-semibold">
                      {externalSource.hashTags?.map((hashTag) => (
                        <div key={hashTag}>
                          <span>{hashTag}</span>
                          <br />
                        </div>
                      ))}
                    </div>

                    {externalSource.imageUrls && externalSource.imageUrls.length > 0 && (
                      <div className="ml-[10px] mb-2 rounded-lg flex">
                        <PostImagesBox
                          images={externalSource.imageUrls.map((imageUrl) =>
                            imageUrl.replaceAll("'", '').trim()
                          )}
                          focusRef={''}
                          enableDelete={false}
                          enableFullSizeViewer={true}
                          onDeletedImage={() => {}}
                        />
                      </div>
                    )}

                    <Link to={externalSource.externalSourceUrl}>
                      <div className="flex items-center gap-1 mx-[10px] mb-4 text-[13px] relative text-sub-light-1 dark:text-sub-dark-1 cursor-pointer font-medium">
                        {t('OfflineLocationDetail.moveToX')}
                        <MoveIcon className="w-[14px] h-[14px] dark:text-main-dark-4" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isShowBottomToast && isEmpty(location.footprints) && (
        <motion.div
          className="fixed w-full max-width-container safeAreaMarginBottom bottom-[96px] z-[10000]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          onClick={() => navigateReportFootprint()}
        >
          <div className="flex flex-row justify-between rounded-[4px] mx-[20px] px-[12px] py-[12px] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.08)] bg-pure-white dark:bg-pure-black mt-[10px]">
            <div className="text-center font-medium text-[14px]">
              {t('OfflineLocationDetail.toastTitle')}
            </div>
            <div className="text-center font-medium text-[13px] text-primary-light dark:text-primary-dark">
              {' '}
              {/* text-primary-light이미 적용중 */}
              {t('report')}
            </div>
          </div>
        </motion.div>
      )}

      <div className="h-24" />
      <LocationBottomBar
        locationId={location.locationId}
        locationName={location.name}
        locationAddress={location.streetAddress}
        coordinates={center}
        kakaoMapUID={location.kakaoUid}
        isLiked={location.isLiked}
      />
    </div>
  );
};

export default OfflineLocationDetailPage;
